import { HttpClient, HttpParams } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { environment } from "../../../../environments/environment";
import { ApiErrorHandlerService } from "../../utils/api-error-handler/api-error-handler.service";
import { map } from "rxjs";
import { Role, RoleDTO } from "../../../models/api/role";

@Injectable({
  providedIn: "root",
})
export class RolesService {
  private http = inject(HttpClient);
  private apiErrorHandler = inject(ApiErrorHandlerService);

  /**
   * Retrieves the list of all roles.
   *
   * @param tenantId - Optional tenant ID for context-based role filtering
   * @param customerId - Optional customer ID for context-based role filtering
   * @returns An observable that emits an array of role objects.
   */
  public getRoles(tenantId?: string, customerId?: string) {
    let params = new HttpParams();

    if (tenantId) params = params.set("tenantId", tenantId);
    if (customerId) params = params.set("customerId", customerId);

    return this.http
      .get<RoleDTO[]>(environment.baseUrlV2 + "/role", {
        params,
      })
      .pipe(
        this.apiErrorHandler.handle("role"),
        map((data: RoleDTO[]) => {
          return data
            .map((roleData) => new Role(roleData))
            .sort((a: Role, b: Role) => {
              if (a.label && b.label) {
                return a.label.localeCompare(b.label);
              } else {
                return 0;
              }
            });
        })
      );
  }

  /**
   * Retrieves a role by its ID.
   *
   * @param id - The ID of the role to retrieve.
   * @returns An observable that emits the role object corresponding to the specified ID.
   */
  public getRoleById(id: string) {
    return this.http.get<RoleDTO>(environment.baseUrlV2 + `/role/${id}`).pipe(
      this.apiErrorHandler.handle("role"),
      map((roleData) => new Role(roleData))
    );
  }

  /**
   * Creates a new role with the given data.
   * @param role The role to be created.
   * @returns The observable of the POST request to the server.
   */
  public createRole(role: Role) {
    return this.http.post<RoleDTO>(environment.baseUrlV2 + "/role", role.apiValues).pipe(
      this.apiErrorHandler.handle("role"),
      map((roleData) => new Role(roleData))
    );
  }

  /**
   * Updates an existing role with the provided role data.
   * @param role The role object containing updated data.
   * @returns The updated role returned by the server.
   */
  public editRole(role: Role) {
    return this.http.put<RoleDTO>(environment.baseUrlV2 + `/role/${role.id}`, role.apiValues).pipe(
      this.apiErrorHandler.handle("role"),
      map((roleData) => new Role(roleData))
    );
  }
}
